import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";
import { AffiliateOrganization } from "schemas/dashboard";

import {
  AFFILIATE_TERMS_AND_CONDITIONS_LINK,
  COMMISSION_PERCENTAGE,
} from "constants/influencer";
import { fetcherAuth } from "utils/api";
import { formatEpochTimestamp } from "utils/time";
import { trackEvent } from "utils/tracking";

type AffiliateReferralCredit = {
  id: number;
  createdAt: number;
  affiliateReferralId: number;
  commissionAmountCents: number;
  bonusCents?: number;
};

type AffiliatePayout = {
  id: number;
  createdAt: number;
  date: string;
  type: "standard" | "cumulative" | "bonus";
  amountCents: number;
};

const styles = {
  promoCode: {
    px: 2,
    py: 1,
    display: "flex",
    border: "2px dashed",
    borderColor: "secondary.main",
    backgroundColor: "secondary.light",
    borderRadius: 2,
    cursor: "pointer",
    maxWidth: 400,
  },
  code: {
    flexGrow: 1,
    mr: 4,
  },
  balance: {
    backgroundColor: "secondary.light",
    p: 3,
    borderRadius: 3,
    maxWidth: 600,
  },
};

export default function AffiliatePartnerView({
  affiliateOrg,
}: {
  affiliateOrg: AffiliateOrganization;
}) {
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const { setAlert, setErrorAlert } = useContext(AlertContext);

  const [fetchLoading, setFetchLoading] = useState(false);
  const [affiliateCredits, setAffiliateCredits] = useState<
    AffiliateReferralCredit[]
  >([]);
  const [affiliatePayouts, setAffiliatePayouts] = useState<AffiliatePayout[]>(
    [],
  );
  const [balance, setBalance] = useState<{
    total_commission: number;
    total_bonus: number;
  }>({ total_commission: 0, total_bonus: 0 });
  const [totalPayoutsByType, setTotalPayoutsByType] = useState<{
    standard: number;
    cumulative: number;
    bonus: number;
  }>({ standard: 0, cumulative: 0, bonus: 0 });
  const [viewCredits, setViewCredits] = useState(false);
  const [viewPayouts, setViewPayouts] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPayoutsCount, setTotalPayoutsCount] = useState(0);

  const fetchAffiliateBalance = async () => {
    setFetchLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/affiliate-organizations/balance`,
      );
      setBalance(res.balance);
      setTotalPayoutsByType(res.totalPayoutsByType);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchTransactions = async (creditId?: number) => {
    try {
      let url = `/api/organization/${currentOrg?.id}/affiliate-organizations/credits?per_page=10`;
      if (creditId) {
        url += `&cursor=${creditId}`;
      }
      const res = await fetcherAuth(getToken, url);
      setAffiliateCredits((prev) => [...prev, ...res.credits]);
      setTotal(res.total);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchPayouts = async (payoutId?: number) => {
    try {
      let url = `/api/organization/${currentOrg?.id}/affiliate-organizations/payouts?per_page=10`;
      if (payoutId) {
        url += `&cursor=${payoutId}`;
      }
      const res = await fetcherAuth(getToken, url);
      setAffiliatePayouts((prev) => [...prev, ...res.payouts]);
      setTotalPayoutsCount(res.total);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const handleCopyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value);

    trackEvent("Affiliate Promo Code Link Copied to Clipboard");

    if (setAlert) setAlert("Copied to clipboard!", "success");
  };

  useEffect(() => {
    if (currentOrg?.id) {
      fetchAffiliateBalance();
      fetchTransactions();
      fetchPayouts();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const getPayoutDescription = (type: AffiliatePayout["type"]) => {
    switch (type) {
      case "bonus":
        return "Additional bonus";
      case "cumulative":
        return "Prior payouts (cumulative)";
      case "standard":
      default:
        return "Affiliate earnings";
    }
  };

  const formatPayoutDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
  };

  // Exclude bonus payouts from unpaid balance calculation
  const totalEarnings = balance.total_commission + balance.total_bonus;
  const unpaidBalance =
    totalEarnings - totalPayoutsByType.standard - totalPayoutsByType.cumulative;

  const formatBalance = (balance: number) => {
    return Number(balance / 100)?.toFixed(2);
  };

  return (
    <Grid container columnGap={2} rowGap={2} alignItems="flex-start">
      <Grid item xs={12} lg>
        <Typography variant="h4" paragraph>
          Welcome to Bento's Affiliate Program
        </Typography>

        <Typography sx={{ mr: 4 }}>
          Earn {COMMISSION_PERCENTAGE}% commission on every payment made by a
          new Bento user who subscribes using your unique promo code - valid for
          their first 12 months.
          <br />
          <ul>
            <li>Refer 5 paying users and earn an additional $50</li>
            <li>Refer 10 paying users and earn an additional $150</li>
            <li>Refer 20 paying users and earn an additional $500</li>
            Promote Bento, get rewarded.{" "}
            <li>Refer 100 paying users and earn an additional $2500 🚀</li>
          </ul>
          Promote Bento, get rewarded.{" "}
          <Link
            target="_blank"
            rel="noreferrer"
            href={AFFILIATE_TERMS_AND_CONDITIONS_LINK}
            sx={{ color: "primary.main", cursor: "pointer" }}
            onClick={() => trackEvent("Affiliate Terms and Conditions Clicked")}
          >
            T&Cs apply.
          </Link>
        </Typography>

        <Typography
          variant="caption"
          component="div"
          color="textSecondary"
          gutterBottom
          sx={{ my: 2 }}
        >
          Here's your unique affiliate link.{" "}
          {affiliateOrg?.stripePromotionCode &&
            "This code can be used by Bento users to get 10% off their first purchase."}
        </Typography>

        <Box
          sx={styles.promoCode}
          onClick={() => handleCopyToClipboard(affiliateOrg.referralLink)}
        >
          <Box sx={styles.code}>{affiliateOrg.referralLink}</Box>
          <Box
            component="i"
            sx={{ lineHeight: "1.25" }}
            className="fa-regular fa-copy fa-lg"
          />
        </Box>

        {affiliateOrg?.stripePromotionCode && (
          <>
            <Typography
              gutterBottom
              component="div"
              sx={{ mt: 2 }}
              variant="caption"
              color="textSecondary"
            >
              Or you can share a coupon code:
            </Typography>
            <Box
              sx={styles.promoCode}
              onClick={() =>
                handleCopyToClipboard(affiliateOrg.stripePromotionCode)
              }
            >
              <Box sx={styles.code}>{affiliateOrg.stripePromotionCode}</Box>
              <Box
                component="i"
                sx={{ lineHeight: "1.25" }}
                className="fa-regular fa-copy fa-lg"
              />
            </Box>
          </>
        )}

        <Box sx={{ mt: 3 }}>
          <Typography>
            <strong>Selling Usage Rights</strong>
          </Typography>

          <Typography>
            <a
              target="_blank"
              href="https://docs.google.com/forms/d/e/1FAIpQLScA75j4KjVTL-SDorbW5vPA5bkxVPEK15R2DIEvOyKJVpwmsg/viewform"
              rel="noreferrer"
            >
              Complete this form
            </a>{" "}
            if you would be interested in selling usage rights for ads for
            content you have made about Bento (you can earn up to{" "}
            <strong>$500</strong> for your content)
          </Typography>
        </Box>
        {!affiliateOrg?.stripePromotionCode && (
          <Box sx={{ mt: 3 }}>
            <Typography>
              <strong>Affiliate Partner Coupon Code</strong>
            </Typography>

            <Typography>
              <a
                target="_blank"
                href="https://docs.google.com/forms/d/e/1FAIpQLSfcgUO62Tgt2aeS2E0XGbjwmcyK90t1p_PLZfrKkJRjBT0ChQ/viewform?usp=dialog"
                rel="noreferrer"
              >
                Complete this form
              </a>{" "}
              if you would like a coupon code to share with your referrals so
              that they get a discount on new subscriptions. You will still earn
              10% of the purchase price.
            </Typography>
          </Box>
        )}
      </Grid>

      <Grid item xs={12} lg>
        <Grid item sx={styles.balance}>
          <Typography variant="h6">💵 Current Balance</Typography>
          <FormHelperText>
            {unpaidBalance < 0 && (
              <>
                We overpaid you in a previous payout. Your next payout will be
                issued once your balance reaches $100 again.{" "}
              </>
            )}
            Your earnings will be paid in accordance to the{" "}
            <Link
              target="_blank"
              rel="noreferrer"
              href={AFFILIATE_TERMS_AND_CONDITIONS_LINK}
              sx={{ color: "primary.main", cursor: "pointer" }}
              onClick={() =>
                trackEvent("Affiliate Terms and Conditions Clicked")
              }
            >
              T&Cs
            </Link>
            .{" "}
            {unpaidBalance >= 0 && (
              <>
                You must have a minimum of $100 in unpaid commissions earned
                before being eligible for a payout.
              </>
            )}
          </FormHelperText>
          {fetchLoading ? (
            <Skeleton height={100} width={"50%"} />
          ) : (
            <Typography variant="h2" sx={{ my: 2 }}>
              ${formatBalance(unpaidBalance)} USD
            </Typography>
          )}

          {totalPayoutsByType.standard + totalPayoutsByType.cumulative > 0 && (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              You've received{" "}
              <strong>
                $
                {formatBalance(
                  totalPayoutsByType.standard + totalPayoutsByType.cumulative,
                )}
              </strong>{" "}
              in affiliate commissions and bonuses
              {totalPayoutsByType.bonus > 0 && (
                <>
                  , plus{" "}
                  <strong>${formatBalance(totalPayoutsByType.bonus)}</strong> in
                  other bonuses
                </>
              )}
              .
            </Typography>
          )}

          {affiliatePayouts?.length > 0 && (
            <Link
              sx={{ cursor: "pointer", textDecoration: "none", fontSize: 12 }}
              onClick={() => {
                setViewPayouts((prev) => !prev);
                trackEvent("My Affiliate Payouts Button Clicked");
              }}
            >
              <strong>{viewPayouts ? "Hide Payouts" : "See Payouts"}</strong>
            </Link>
          )}

          {viewPayouts && affiliatePayouts && (
            <>
              <Table size="small" sx={{ maxWidth: 500, mt: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {affiliatePayouts.map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell>{formatPayoutDate(row.date)}</TableCell>
                      <TableCell>{getPayoutDescription(row.type)}</TableCell>
                      <TableCell>${formatBalance(row.amountCents)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}

          {totalPayoutsCount > affiliatePayouts?.length && viewPayouts && (
            <Button
              onClick={() =>
                fetchPayouts(
                  affiliatePayouts?.[affiliatePayouts?.length - 1]?.id,
                )
              }
              size="small"
              sx={{ fontSize: 12, textTransform: "none" }}
              color="secondary"
            >
              View more
            </Button>
          )}
        </Grid>

        <Grid item sx={{ ...styles.balance, mt: 2 }}>
          <Typography variant="h6">💰 Lifetime Earnings</Typography>
          <FormHelperText>
            Your lifetime commissions and bonuses earned from referrals.
          </FormHelperText>
          {fetchLoading ? (
            <Skeleton height={100} width={"50%"} />
          ) : (
            <Typography variant="h2" sx={{ my: 2 }}>
              ${formatBalance(balance.total_commission + balance.total_bonus)}{" "}
              USD
            </Typography>
          )}

          {affiliateCredits?.length > 0 && (
            <Link
              sx={{ cursor: "pointer", textDecoration: "none", fontSize: 12 }}
              onClick={() => {
                setViewCredits((prev) => !prev);
                trackEvent("My Affiliate Credits Button Clicked");
              }}
            >
              <strong>{viewCredits ? "Hide" : "See Breakdown"}</strong>
            </Link>
          )}

          {viewCredits && affiliateCredits && (
            <Table size="small" sx={{ maxWidth: 500 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Commission</TableCell>
                  <TableCell>Bonus</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {affiliateCredits.map((row) => (
                  <TableRow
                    key={row.id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell>
                      {formatEpochTimestamp({
                        epochTimestamp: row.createdAt,
                        addYear: true,
                      })}
                    </TableCell>
                    <TableCell>User paid an invoice</TableCell>
                    <TableCell>
                      ${formatBalance(row.commissionAmountCents)}
                    </TableCell>
                    <TableCell>${formatBalance(row.bonusCents || 0)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {total > affiliateCredits?.length && viewCredits && (
            <Button
              onClick={() =>
                fetchTransactions(
                  affiliateCredits?.[affiliateCredits?.length - 1]?.id,
                )
              }
              size="small"
              sx={{ fontSize: 12, textTransform: "none" }}
              color="secondary"
            >
              View more
            </Button>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}
